import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Optional, Output, Self
} from '@angular/core';
import {
  ControlValueAccessor, NgControl
} from '@angular/forms';
import {Base} from '../../../common/model/base.model';
import {AuthService} from '../../../auth/auth.service';
import {ValidationHelper} from '../../../common/validation/validation-helper';
import {Enum} from '../../../common/model/enum.model';
import {ConfigService} from '../../../core/config.service';
import {Select2AngularComponent} from './select2-angular.component';

// declare var jquery: any;   // not required
declare var $: any;   // not required

@Component({
    selector: 'app-control-select-model',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
      <app-form-layout [controlDir]="controlDir"
                       [inline]="inline"
                       [label]="label"
                       [noLabel]="noLabel"
                       [ttip]="ttip"
                       [ttipAppend]="ttipAppend"
                       [showValidation]="showValidation"
                       >
          <div [ngClass]="divSelectClass()">
            <div *ngIf="prepend || prependText" class="input-group-prepend" tooltip="{{ ttip | translate }}">
              <div class="input-group-text">
                <i class="fa fa-{{ prepend }}" aria-hidden="true"></i>
                {{ prependText }}
              </div>
            </div>
          
            <select class="custom-select select2-single"
                    [ngClass]="selectClasses()">
              <option *ngIf="allowClear"></option>
              <option *ngFor="let item of items" [value]=item.getId() [selected]="isItemSelected(item.getId())">
                  {{ item.getLabel(detailedLabel) }}
              </option>

              <option *ngFor="let item of enums" [value]=item.getEnumValue() [selected]="isEnumSelected(item.getEnumValue())">
                {{ item.getLabelValue() | translate }}
              </option>

              <!--<option *ngIf="isSingleAsync() && selected" [value]="selected"> {{ selected.getLabel() }}</option>-->
            </select>

            <div *ngIf="append || appendText" class="input-group-append" tooltip="{{ ttipAppend | translate }}" placement="left">
              <div class="input-group-text">
                <i *ngIf="append" class="fa fa-{{ append }}" aria-hidden="true"></i>
                {{ appendText }}
              </div>
            </div>
          </div>

          <div *ngIf="showList()" class="col-12 p-0 mt-0">
            <ul class="list-group">
              <li *ngFor="let s of showListItems()" class="list-group-item pt-1 pb-1 pr-1">
                <div class="d-flex">
                  <div *ngIf="template" [innerHtml]=template(s)></div>
                  <div *ngIf="!template">
                    {{ s.getLabel() }}
                  </div>
                  <div class="ml-auto" *ngIf="!disable">
                    <button *ngIf="!this.multiple && !editing" type="button" class="btn btn-outline-primary btn-sm pt-0 pb-0 pl-1 pr-1"
                            (click)="editing=true" title="Modifica">
                      <i class="fa fa-pencil-square-o"></i>
                    </button>
                    <button *ngIf="editing" type="button" class="btn btn-outline-secondary btn-sm pt-0 pb-0 pl-1 pr-1"
                              (click)="editing=false" title="Annulla Modifica">
                      <i class="fa fa-undo"></i>
                    </button>
                    <button *ngIf="!editing" type="button" class="btn btn-outline-danger btn-sm pt-0 pb-0 pl-1 pr-1"
                              (click)="unselect(s)" title="Elimina">
                      <i class="fa fa-times"></i>
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </div>
      </app-form-layout>
    `,
    standalone: false
})
export class FormTemplateSelectModelComponent extends Select2AngularComponent implements ControlValueAccessor, OnInit, OnChanges {

  @Input() items: Base[];

  @Input() enums: Enum[];

  @Input() showAsList: false;

  @Input() detailedLabel: false;

  @Output() onChangeEvent = new EventEmitter<any>();

  selected;

  editing: false;

  /**
   * Self permette di poter innestare form controls... ci assicuriamo
   * che sia esattamente quello fornito dal parent.
   * @param {NgControl} controlDir
   */
  constructor(@Optional() @Self() public controlDir: NgControl,
              public ref: ChangeDetectorRef,
              protected authService: AuthService,
              protected configService: ConfigService) {
    super(authService, configService);
    controlDir.valueAccessor = this;
  }

  /**
   * Implementazione Hook angular.
   */
  ngOnInit() {
    const control = this.controlDir.control;

    // se volessi aggiungere dei validatori di default al tag, nell'esempio required.
    // const validators = control.validator ? [control.validator, Validators.required] : Validators.required;
    const validators = control.validator;
    // console.log('I validatori sono', validators);
    control.setValidators(validators);
    control.updateValueAndValidity();

    this.initializeSelect2();
  }

  ngOnChanges() {
    if (!this.asyncInitialized) {
      this.initializeSelect2();
    }
  }

  /**
   * Implementazione callback necessarie alla select2.
   */
   public select(e) {

    const data = e.params.data;

    this.editing = false;

    if (this.isSingleSync()) {
      this.updateSigleSync(data);
      return;
    }

    if (this.isSingleAsync()) {
      this.updateSigleAsync(data);
      return;
    }

    if (this.isMultiSync()) {
      this.updateMultiSync(data);
      return;
    }

    if (this.isMultiAsync()) {
      this.updateMultiAsync(data);
      return;
    }

    if (this.isEnum()) {
      this.updateEnum(data);
      return;
    }
  }

  public unselect(e) {

    if (this.multiple) {
      const index = this.selected.indexOf(e, 0);
      if (index > -1) {
        this.selected.splice(index, 1);
      }

    } else {
      this.selected = null;
      const s = this.cssSelector();
      $(s).val(null).trigger('change');
    }

    this.controlDir.control.updateValueAndValidity();

    this.completeOnChange();
  }

  protected getSelected() {
    return this.selected;
  }

  private isSingleSync() {
    return this.items && !this.multiple && !this.path;
  }

  private isSingleAsync() {
    return !this.multiple && this.path;
  }

  private isEnum() {
    return this.enums;
  }

  private isMultiSync() {
    return this.multiple && !this.path;
  }

  private isMultiAsync() {
    return this.multiple && this.path;
  }

  private updateSigleSync(data) {
    // same value prevent change detection
    if (this.selected && this.selected.getId && this.selected.getId() === +data.id) {
      return;
    }
    // update value
    this.selected = this.items.filter((item) => item.getId() === +data.id)[0];

    if (this.template) {
      const s = this.cssSelector();
      $(s).val(this.selected.id).trigger('change');
    }
        
    this.completeOnChange();
  }

  private updateSigleAsync(data) {
    // same value prevent change detection
    if (this.selected && this.selected.getId && this.selected.getId() === +data.id) {
      return;
    }

    // Update value
    this.selected = this.buildValue(data);

    this.completeOnChange();
  }

  private updateEnum(data) {
    // same value prevent change detection
    if (this.selected && this.selected.getEnumValue && this.selected.getEnumValue() === data.id) {
      return;
    }
    // update value
    this.selected = this.enums.filter( (en) => en.getEnumValue() === data.id)[0];

    this.completeOnChange();
  }

  private updateMultiSync(data) {
    const s = this.cssSelector();
    $(s).val(null).trigger('change');

    if (!this.selected) {
      this.selected = [];
    }

    // same value prevent change detection
    if (this.selected.length > 0) {
      const values = this.selected.filter( item =>  item.getId() === +data.id);
      if (values.length > 0) {
        return;
      }
    }

    // update value
    this.selected.push(this.items.filter((item) => item.getId() === +data.id)[0]);

    this.completeOnChange();
  }

  private updateMultiAsync(data) {

    const s = this.cssSelector();
    $(s).val(null).trigger('change');

    if (!this.selected) {
      this.selected = [];
    }

    // same value prevent change detection
    if (this.selected.length > 0) {
      const values = this.selected.filter( item =>  item.getId() === +data.id);
      if (values.length > 0) {
        return;
      }
    }

    // update value
    this.selected.push(this.buildValue(data));

    this.completeOnChange();
  }

  public completeOnChange() {
    this.onChange(this.selected);
    this.ref.detectChanges();
    this.onChangeEvent.emit(this.selected);
    this.initializeSelect2();
  }

  /*
  ControlValueAccess Impl.
   */

  onChange(value: Base) {}

  onTouched = () => {};

  writeValue(value: any): void {

    this.selected = value;

    if (this.asyncInitialized) {
      // Quando injetto nuovi valori nella form async devo creare le option, per comodità la reinizializzo.
      this.asyncInitialized = false;
      $(this.cssSelector()).data('select2').destroy();
      this.initializeSelect2();
    }

    if (this.controlDir.control) {
      this.controlDir.control.markAsDirty();
      this.controlDir.control.updateValueAndValidity();
    }

    this.ref.detectChanges();
  }

  registerOnChange(fn: (value: Base) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /*
  Utils.
   */

  isInvalid(): boolean {
    return ValidationHelper.showInvalid(this.controlDir, this.showValidation);
  }

  isValid(): boolean  {
    return ValidationHelper.showValid(this.controlDir, this.showValidation);
  }

  isItemSelected(value: any) {
    if (this.isSingleSync() || this.isSingleAsync()) {
      return this.selected ? this.selected.getId() === value : false;
    }
    if (this.isMultiSync()) {
      return false; // this.selected ? this.selected.filter(item => item === value).lenght > 0 : false;
    }
  }

  isEnumSelected(value: any) {
    return this.selected ? this.selected.getEnumValue() === value : false;
  }

  selectClasses() {
    const classes = {
      'is-valid': this.isValid(),
      'is-invalid': this.isInvalid(),
    };
    classes[this.id] = true;
    return classes;
  }

  showList() {
    if (this.multiple) {
      return this.selected && this.selected.length > 0;
    }
    if (this.showAsList && this.selected) {
      return true;
    }
    return false;
  }

  showListItems() {
    if (this.multiple) {
      return this.selected;
    }
    if (this.selected) {
      return [this.selected];
    }
    return [];
  }

  buildValue(data) {
    if (this.baseBuilder && data.entity) {
      return this.baseBuilder(data.entity);
    }
    return data.id;
  }

  divSelectClass() {
    const classes = {
      'input-group': true,
      'd-none': !this.showSelect()
    };
    return classes;
  };

  showSelect() {
    return this.multiple || !this.showAsList || this.editing || this.selected == null;
  };
}

